import {getReactSelectDefaultProps, ReactSelectChangeHandlerOptions, ReactSelectProps} from "config/react-select";
import {EntryDescSelectGroup} from "module/edesc/component/EntryDescSelectGroup";
import {EntryDescSelectMenuList} from "module/edesc/component/EntryDescSelectMenuList";
import {EntryDescSelectOption} from "module/edesc/component/EntryDescSelectOption";
import {EntryDescFavSelectPlaceholder} from "module/edesc/component/EntryDescFavSelectPlaceholder";
import {TypeColor} from "module/edesc/TypeColor";
import React, {CSSProperties, useCallback, useMemo, useRef} from "react";
import Select, {createFilter, CSSObjectWithLabel} from 'react-select';
import {setEntryDescFavEditModeAction, toggleEntryDescFavAction} from 'module/edesc/action/EntryDescActions';
import {EntryDesc} from 'generated/JavaTOmodifications'
import {useDispatch, useSelector} from 'react-redux'
import {selEntryDescAllIds, selEntryDescById, selEntryDescFav, selEntryDescFavEditMode} from 'module/edesc/selector/EntryDescSelector'
import {selFavDefault} from 'module/edesc/selector/EntryDescFavSelector'
import _ from 'lodash'
import {Perm, TypePerm} from 'module/edesc/TypePerm'
import {createEntryDescSelectDatasource} from 'module/edesc/util/createEntryDescSelectDatasource'
import {SisName} from 'util/SisName'
import {GroupBase} from 'react-select/dist/declarations/src/types'
import {SetCb} from 'type/SetCb'
import {focusNextFormElement} from 'util/view/focusNextFormElement'
import {FilterOptionOption} from 'react-select/dist/declarations/src/filters'


const MAX_FAV_SHORTCUTS = 20;
const MOD = 4;

const customComponents = {
	Group: EntryDescSelectGroup,
	Placeholder: EntryDescFavSelectPlaceholder,
	MenuList: EntryDescSelectMenuList,
	Option: EntryDescSelectOption,
}

function useBaseProps(components: ReactSelectProps<EntryDesc, false>['components'], stylesOverride?: CSSProperties): ReactSelectProps<EntryDesc, false> {
	return useMemo(() => ({
		components,
		styles: {
			valueContainer: (styles: CSSObjectWithLabel, {getValue, selectProps}: ReactSelectProps<EntryDesc>) => {
				const entryDesc = getValue()[0];
				const v = entryDesc && MOD || 0;
				const tc = entryDesc && TypeColor.toCSS(entryDesc, (selectProps as { dev?: string }).dev);
				return ({
					...styles,
					...tc,
					marginLeft: v,
					borderRadius: v,
				})
			},
			singleValue: (styles: CSSObjectWithLabel, {data: entryDesc, selectProps}: ReactSelectProps<EntryDesc>) => ({
				...TypeColor.toCSS(entryDesc, (selectProps as { dev?: string }).dev),
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				maxWidth: 'calc(100% - 7px)', // fixes line break on long text/small window
				position: 'absolute',
				padding: '0 8px',
			}),
			option: () => ({
				padding: 0,
			}),
			...stylesOverride,
		},
	}), [components, stylesOverride])
}

export interface EntryDescSelectProps {
	disabled?: boolean
	autoFocus: boolean
	modelDev: string | undefined
	modelEntryDescId: number | null | undefined
	modelSelfAdmin: boolean | undefined
	persistedModelEntryDescId: number | null | undefined
	persistedModelSisName: SisName | undefined
	setter: SetCb<EntryDesc | undefined>
}

export interface EntryDescSelectCustomProps {
	dev?: string
	entryDescShortcuts?: EntryDesc[]
	setEntryDesc?: (ed?: EntryDesc) => void
	entryDescFavIds?: number[]
	entryDescFavEditMode?: boolean
	toggleEntryDescFav?: (arg: Parameters<typeof toggleEntryDescFavAction>[0]) => any,
	setEntryDescFavEditMode?: (arg: Parameters<typeof setEntryDescFavEditModeAction>[0]) => void
}

export const EntryDescSelect = React.memo<EntryDescSelectProps>(({
	disabled,
	modelDev,
	modelEntryDescId,
	modelSelfAdmin,
	persistedModelEntryDescId,
	persistedModelSisName,
	setter,
	autoFocus,
}) => {
	const dispatch = useDispatch()
	const ref = useRef<typeof Select<EntryDesc, false> & { inputRef?: HTMLInputElement }>(null);
	const byId = useSelector(selEntryDescById)
	const allIds = useSelector(selEntryDescAllIds)
	let fav = useSelector(selEntryDescFav)
	const favEditMode = useSelector(selEntryDescFavEditMode)
	const favDefault = useSelector(selFavDefault)

	let onChange = useCallback((value: EntryDesc, {action}: ReactSelectChangeHandlerOptions) => {
		switch (action) {
			case 'create-option':
				break;
			case 'select-option':
				setter(value);
				if (ref.current?.inputRef) focusNextFormElement(1, {inputEl: ref.current?.inputRef, focusNextOnlyInForm: true}, 100); // 100ms delay: wait for rendered fields
				break;
			case 'deselect-option':
			case 'clear':
				setter(undefined);
				break;
			case 'remove-value':
				break;
			case 'pop-value':
				break;
			case 'set-value':
				break;
			default:
				console.error('unknown action')
		}
	}, [ref.current])

	let options;
	let entryDescShortcuts: EntryDesc[]
	if (!favEditMode) {
		const permittedEntryDescs = _.compact(_.map(allIds, id => {
			let ed = byId[id];
			if (id === persistedModelEntryDescId) return ed;
			if (persistedModelSisName && ed?.sisName !== persistedModelSisName) return undefined; // filter
			if (TypePerm.check(ed, Perm.crt, modelSelfAdmin)) return ed;
		}));
		const permittedEntryDescIds = permittedEntryDescs.map(x => x.id);
		if (_.isEmpty(fav)) fav = favDefault;
		let permittedFavIds = _.intersection(fav, permittedEntryDescIds);
		if (!permittedFavIds.length) {
			permittedFavIds = permittedEntryDescIds.slice(0, MAX_FAV_SHORTCUTS);
		}
		entryDescShortcuts = permittedFavIds.slice(0, MAX_FAV_SHORTCUTS).map(x => byId[x]).filter(x => !!x) as EntryDesc[]
		options = createEntryDescSelectDatasource(permittedEntryDescs);
	} else {
		//show all fav for edit mode
		const anyCreatable = allIds.flatMap(id => {
			const ed = byId[id]
			return (ed && ed.perm && (ed.perm.crt || ed.perm.crt_s)) ? [ed] : []
		})
		entryDescShortcuts = _.compact(_.map(fav, edId => byId[edId]));
		options = createEntryDescSelectDatasource(entryDescShortcuts.concat(anyCreatable.filter(ed => Boolean(ed && !_.includes(fav, ed.id))) as EntryDesc[]))
		onChange = () => void 0
	}

	return <Select
		{...getReactSelectDefaultProps<EntryDesc, false, GroupBase<EntryDesc>, EntryDescSelectCustomProps>({
			...useBaseProps(customComponents),
			ref: ref,
			isMulti: false,
			isClearable: true,
			isDisabled: disabled,
			options: options,
			getOptionValue: (ed: EntryDesc) => ed.id + '',
			getOptionLabel: (ed: EntryDesc) => ed.label,
			value: !favEditMode && (modelEntryDescId || modelEntryDescId === 0) ? byId[modelEntryDescId] : null,
			filterOption: createFilter({
				stringify: (option: FilterOptionOption<EntryDesc>) => option.label + ' ' + option.value + ' ' + option.data.categoryLabel,
			}),
			menuIsOpen: favEditMode || undefined, // keep open when in favEditMode
			blurInputOnSelect: true,
			dev: modelDev,
			entryDescShortcuts: entryDescShortcuts || [],
			setEntryDesc: useCallback((ed?: EntryDesc) => {
				setter(ed)
				if (ref.current?.inputRef) focusNextFormElement(1, {inputEl: ref.current?.inputRef, focusNextOnlyInForm: true}, 100); // 100ms delay: wait for rendered fields
			}, [ref.current?.inputRef]),
			entryDescFavIds: fav,
			entryDescFavEditMode: favEditMode,
			toggleEntryDescFav: useCallback((arg: Parameters<typeof toggleEntryDescFavAction>[0]) => dispatch(toggleEntryDescFavAction(arg)), []),
			setEntryDescFavEditMode: useCallback((arg: Parameters<typeof setEntryDescFavEditModeAction>[0]) => dispatch(setEntryDescFavEditModeAction(arg)), []),
			onChange: onChange,
			autoFocus,
		})}
	/>
})
